import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TextInput, View, TouchableOpacity } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FontFamily } from '../../../constants/fonts';
import { TeamWorkloadMemberData } from './types';
import SkeletonBlock from './SkeletonBlock';

interface TeamWorkloadMembersSectionProps {
  members: TeamWorkloadMemberData[];

  /**
   * When true the table body is replaced by shimmering placeholder rows that
   * mirror the real column layout, so the card keeps its size while loading.
   */
  loading?: boolean;

  /** Number of placeholder rows rendered while loading. */
  skeletonRowCount?: number;
  /** Optional error message to display inside the card when the request failed. */
  error?: string | null;
  /** Called when the user taps the Retry button shown in the error state. */
  onRetry?: () => void;
  /** When true, shows the filter-hint empty message instead of the generic one. */
  hasActiveFilters?: boolean;
}

/** Header labels shared by the real table and its skeleton. */
const HEADER_LABELS: Array<{ key: string; label: string }> = [
  { key: 'teamMember', label: 'Team Member' },
  { key: 'assignedTickets', label: 'Assigned Tickets' },
  { key: 'openTickets', label: 'Open Tickets' },
  { key: 'closedTickets', label: 'Closed Tickets' },
  { key: 'averageResolutionTime', label: 'Average Resolution Time' },
  { key: 'averageTimeInProgress', label: 'Avg Time in Progress' },
  { key: 'averageTimeEscalatedToIT', label: 'Avg Time in Escalated to IT' },
  { key: 'averageTimeEscalatedToTMV', label: 'Avg Time in Escalated to TMV' },
  { key: 'averageTimeSubmittedToADO', label: 'Avg Time in Submitted to ADO' },
  { key: 'averageTimeWaitingOnUser', label: 'Avg Time in Waiting on User' },
  { key: 'resolutionRate', label: 'Resolution Rate' },
];

/**
 * Column widths shared by the header, body cells and skeleton — sized so the
 * full uppercase header label plus the sort arrow always fits on one line
 * (no truncation), matching the StaleTicketsSection pattern.
 */
const COLUMN_WIDTHS: number[] = [150, 190, 150, 170, 260, 220, 260, 270, 270, 270, 170];

/**
 * Placeholder table shown while team-member rows load.
 *
 * Lives in this file so any consumer gets the skeleton simply by passing
 * `loading`, and so it can reuse the exact row / column-width styles below.
 */
const MembersSkeleton: React.FC<{ rowCount: number }> = ({ rowCount }) => (
  <View style={styles.card}>
    <View style={styles.cardHead}>
      <SkeletonBlock style={styles.skeletonTitle} />
    </View>

    <ScrollView horizontal showsHorizontalScrollIndicator={true}>
      <View style={styles.tableWrap}>
        {/* Real header row keeps the column geometry identical to the loaded table. */}
        <View style={[styles.row, styles.headerRow]}>
          {HEADER_LABELS.map((column, index) => (
            <View
              key={column.key}
              style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[index] }]}
            >
              <Text style={[styles.headerCell, index !== 0 && styles.numText]}>{column.label}</Text>
            </View>
          ))}
        </View>

        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          <View style={styles.row} key={`members-skeleton-row-${rowIndex}`}>
            {HEADER_LABELS.map((column, columnIndex) => (
              <View
                key={`members-skeleton-cell-${rowIndex}-${columnIndex}`}
                style={[
                  styles.skeletonCell,
                  { width: COLUMN_WIDTHS[columnIndex] },
                  columnIndex !== 0 && styles.skeletonCellRight,
                ]}
              >
                <SkeletonBlock
                  style={[styles.skeletonText, { width: columnIndex === 0 ? 110 : 56 }]}
                />
              </View>
            ))}
          </View>
        ))}
      </View>
    </ScrollView>
  </View>
);


const MembersSection: React.FC<TeamWorkloadMembersSectionProps> = ({
  members,
  loading = false,
  skeletonRowCount = 8,
  error = null,
  onRetry,
  hasActiveFilters = false,
}) => {
  const [sortKey, setSortKey] = useState<string>('teamMember');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchText, setSearchText] = useState('');

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    const defaultAsc = ['teamMember', 'assignedTickets', 'openTickets', 'closedTickets', 'resolutionRate'].includes(key);
    setSortKey(key);
    setSortDirection(defaultAsc ? 'asc' : 'desc');
  };

  const filteredMembers = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return members;
    }

    return members.filter((item) => String(item.teamMember ?? '').toLowerCase().includes(query));
  }, [members, searchText]);

  const sortedMembers = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;

    const getComparable = (item: any) => {
      switch (sortKey) {
        case 'teamMember':
          return String(item.teamMember ?? '').toLowerCase();
        case 'assignedTickets':
          return Number(item.assignedTickets) || 0;
        case 'openTickets':
          return Number(item.openTickets) || 0;
        case 'closedTickets':
          return Number(item.closedTickets) || 0;
        case 'averageResolutionTime':
          return Number(item.averageResolutionTimeValue) || 0;
        case 'averageTimeInProgress':
          return Number(item.averageTimeInProgressValue) || 0;
        case 'averageTimeEscalatedToIT':
          return Number(item.averageTimeEscalatedToITValue) || 0;
        case 'averageTimeEscalatedToTMV':
          return Number(item.averageTimeEscalatedToTMVValue) || 0;
        case 'averageTimeSubmittedToADO':
          return Number(item.averageTimeSubmittedToADOValue) || 0;
        case 'averageTimeWaitingOnUser':
          return Number(item.averageTimeWaitingOnUserValue) || 0;
        case 'resolutionRate':
          return Number(item.resolutionRate) || 0;
        default:
          return String(item.teamMember ?? '').toLowerCase();
      }
    };

    return [...filteredMembers].sort((a, b) => {
      const left = getComparable(a);
      const right = getComparable(b);

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * dir;
      }

      return String(left).localeCompare(String(right)) * dir;
    });
  }, [filteredMembers, sortKey, sortDirection]);

  const sortIndicator = (key: string) => (sortKey === key ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '');

  if (loading) {
    return <MembersSkeleton rowCount={skeletonRowCount} />;
  }

  if (error) {
    return (
      <View style={styles.card}>
        <View style={styles.cardHead}>
          <Text style={styles.cardTitle}>Tickets by Team Member</Text>
        </View>
        <View style={styles.errorWrap}>
          <Text style={styles.errorText}>{error}</Text>
          {!!onRetry && (
            <TouchableOpacity onPress={onRetry} style={styles.retryButton} accessibilityRole="button" accessibilityLabel="Retry">
              <Text style={styles.retryText}>↻  Retry</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardTitle}>Tickets by Team Member</Text>

        <View style={styles.searchWrap}>
          <Svg width={15} height={15} viewBox="0 0 24 24" style={styles.searchIcon}>
            <Path
              d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
              fill="#818181"
            />
          </Svg>
          <TextInput
            value={searchText}
            onChangeText={setSearchText}
            placeholder="Search team member"
            placeholderTextColor="#8A8E94"
            style={styles.searchInput}
          />
        </View>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={true} contentContainerStyle={styles.scrollContent}>
        <View style={styles.tableWrap}>
          <View style={[styles.row, styles.headerRow]}>
            <TouchableOpacity onPress={() => handleSort('teamMember')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[0] }]}>
              <Text style={styles.headerCell}>Team Member{sortIndicator('teamMember')}</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => handleSort('assignedTickets')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[1] }]}>
              <Text style={[styles.headerCell, styles.numText]}>Assigned Tickets{sortIndicator('assignedTickets')}</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => handleSort('openTickets')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[2] }]}>
              <Text style={[styles.headerCell, styles.numText]}>Open Tickets{sortIndicator('openTickets')}</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => handleSort('closedTickets')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[3] }]}>
              <Text style={[styles.headerCell, styles.numText]}>Closed Tickets{sortIndicator('closedTickets')}</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => handleSort('averageResolutionTime')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[4] }]}>
              <Text style={[styles.headerCell, styles.numText]}>Average Resolution Time{sortIndicator('averageResolutionTime')}</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => handleSort('averageTimeInProgress')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[5] }]}>
              <Text style={[styles.headerCell, styles.numText]}>Avg Time in Progress{sortIndicator('averageTimeInProgress')}</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => handleSort('averageTimeEscalatedToIT')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[6] }]}>
              <Text style={[styles.headerCell, styles.numText]}>Avg Time in Escalated to IT{sortIndicator('averageTimeEscalatedToIT')}</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => handleSort('averageTimeEscalatedToTMV')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[7] }]}>
              <Text style={[styles.headerCell, styles.numText]}>Avg Time in Escalated to TMV{sortIndicator('averageTimeEscalatedToTMV')}</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => handleSort('averageTimeSubmittedToADO')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[8] }]}>
              <Text style={[styles.headerCell, styles.numText]}>Avg Time in Submitted to ADO{sortIndicator('averageTimeSubmittedToADO')}</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => handleSort('averageTimeWaitingOnUser')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[9] }]}>
              <Text style={[styles.headerCell, styles.numText]}>Avg Time in Waiting on User{sortIndicator('averageTimeWaitingOnUser')}</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => handleSort('resolutionRate')} style={[styles.headerCellWrap, { width: COLUMN_WIDTHS[10] }]}>
              <Text style={[styles.headerCell, styles.numText]}>Resolution Rate{sortIndicator('resolutionRate')}</Text>
            </TouchableOpacity>
          </View>

          {sortedMembers.length ? (
            <ScrollView style={styles.bodyScroll} nestedScrollEnabled>
              <View style={styles.tableBody}>
                {sortedMembers.map((item, index) => (
                  <View style={styles.row} key={`${item.teamMember}-${index}`}>
                    <Text style={[styles.cell, { width: COLUMN_WIDTHS[0] }]}>{item.teamMember}</Text>
                    <Text style={[styles.cell, styles.numText, { width: COLUMN_WIDTHS[1] }]}>{item.assignedTickets}</Text>
                    <Text style={[styles.cell, styles.numText, { width: COLUMN_WIDTHS[2] }]}>{item.openTickets}</Text>
                    <Text style={[styles.cell, styles.numText, { width: COLUMN_WIDTHS[3] }]}>{item.closedTickets}</Text>
                    <Text style={[styles.cell, styles.numText, { width: COLUMN_WIDTHS[4] }]}>{formatDuration(item.averageResolutionTimeValue, item.averageResolutionTimeUnit)}</Text>
                    <Text style={[styles.cell, styles.numText, { width: COLUMN_WIDTHS[5] }]}>{formatDuration(item.averageTimeInProgressValue, item.averageTimeInProgressUnit)}</Text>
                    <Text style={[styles.cell, styles.numText, { width: COLUMN_WIDTHS[6] }]}>{formatDuration(item.averageTimeEscalatedToITValue, item.averageTimeEscalatedToITUnit)}</Text>
                    <Text style={[styles.cell, styles.numText, { width: COLUMN_WIDTHS[7] }]}>{formatDuration(item.averageTimeEscalatedToTMVValue, item.averageTimeEscalatedToTMVUnit)}</Text>
                    <Text style={[styles.cell, styles.numText, { width: COLUMN_WIDTHS[8] }]}>{formatDuration(item.averageTimeSubmittedToADOValue, item.averageTimeSubmittedToADOUnit)}</Text>
                    <Text style={[styles.cell, styles.numText, { width: COLUMN_WIDTHS[9] }]}>{formatDuration(item.averageTimeWaitingOnUserValue, item.averageTimeWaitingOnUserUnit)}</Text>
                    <Text style={[styles.cell, styles.numText, { width: COLUMN_WIDTHS[10] }]}>{item.resolutionRate.toFixed(1)}%</Text>
                  </View>
                ))}
              </View>
            </ScrollView>
          ) : (
            <View style={styles.emptyWrap}>
              <Text style={styles.noResults}>{searchText ? `No results found for "${searchText}"` : 'Try expanding your date range or removing some filters.'}</Text>
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const formatDuration = (value: number, unit: string) => {
  const normalized = Number.isFinite(value) ? value : 0;
  // Convert hours to fraction of 1 day (24 hours) with 2 decimal places
  // Example: 20 hours = 0.83 days, 34.45 hours = 1.44 days
  const daysDecimal = unit === 'hours' ? normalized / 24 : normalized;
  return `${daysDecimal.toFixed(2)} days`;
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8EDF3',
    marginBottom: 16,
    overflow: 'hidden',
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
    rowGap: 10,
    columnGap: 12,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#2D2F33',
  },
  searchWrap: {
    width: 260,
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 6,
    paddingHorizontal: 12,
    height: 32,
    backgroundColor: '#FFFFFF',
  },
  searchIcon: {
    flexShrink: 0,
  },
  searchInput: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
    paddingVertical: 0,
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  tableWrap: {
    minWidth: 2450,
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  tableBody: {
    width: '100%',
  },
  bodyScroll: {
    maxHeight: 496,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2F6',
    minHeight: 48,
  },
  headerRow: {
    borderBottomColor: '#DCE3EC',
    borderBottomWidth: 2,
    minHeight: 56,
  },
  cell: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
    paddingVertical: 12,
    paddingHorizontal: 12,
    textAlignVertical: 'center',
  },
  headerCell: {
    fontSize: 11,
    color: '#6A6D71',
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  headerCellWrap: {
    paddingVertical: 16,
    paddingHorizontal: 12,
    justifyContent: 'center',
  },
  errorWrap: {
    minHeight: 120,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: '#B42318',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#1D4ED8',
    alignSelf: 'center',
  },
  retryText: {
    color: '#1D4ED8',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  emptyWrap: {
    minHeight: 160,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noResults: {
    fontSize: 14,
    color: '#7A8088',
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },

  // ─── Alignment ────────────────────────────────────────────────────────────
  // Width is applied inline per-column (see COLUMN_WIDTHS) since header/body/
  // skeleton all share the exact same widths; this only carries the
  // right-alignment for numeric column text.
  numText: { textAlign: 'right' },
  skeletonTitle: {
    width: 190,
    height: 14,
  },
  skeletonCell: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    justifyContent: 'center',
  },
  skeletonCellRight: {
    alignItems: 'flex-end',
  },
  skeletonText: {
    height: 10,
  },
});

export default MembersSection;






